<template>
  <div
    :class="[
      'p-inplace',
      className,
      `p-inplace-${size}`,
      {
        'p-inplace-closable': closable,
        'p-inplace-disabled': disabled,
        'p-inplace-responsive': responsive,
        'p-inplace-rtl': dir === 'rtl'
      }
    ]"
    :style="style"
  >
    <div
      v-if="!active"
      :class="['p-inplace-display', displayClass]"
      :role="disabled ? undefined : 'button'"
      :tabindex="disabled ? undefined : '0'"
      :aria-label="displayAriaLabel"
      @click="onDisplayClick"
      @keydown.enter="onDisplayClick"
    >
      <slot name="display">
        <span class="p-inplace-display-text">{{ displayAriaLabel }}</span>
      </slot>
    </div>
    <div
      v-else
      :class="['p-inplace-content', contentClass]"
      :role="'region'"
      :aria-label="contentAriaLabel"
    >
      <slot name="content"></slot>
      <div v-if="closable" class="p-inplace-actions">
        <button
          type="button"
          class="p-inplace-save"
          :aria-label="saveAriaLabel"
          @click="onSave"
          @keydown.enter="onSave"
        >
          <span class="pi pi-check"></span>
        </button>
        <button
          type="button"
          class="p-inplace-cancel"
          :aria-label="cancelAriaLabel"
          @click="onCancel"
          @keydown.enter="onCancel"
        >
          <span class="pi pi-times"></span>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { MOPInplaceProps } from './types'

export default defineComponent({
  name: 'MOPInplace',
  inheritAttrs: false,
  emits: ['update:active', 'open', 'close']
})
</script>

<script setup lang="ts">
const props = withDefaults(defineProps<MOPInplaceProps>(), {
  active: false,
  closable: true,
  disabled: false,
  className: undefined,
  style: undefined,
  displayClass: undefined,
  contentClass: undefined,
  displayAriaLabel: 'Click to edit',
  contentAriaLabel: 'Edit content',
  responsive: true,
  dir: 'ltr',
  size: 'normal',
  editAriaLabel: 'Edit',
  saveAriaLabel: 'Save',
  cancelAriaLabel: 'Cancel'
})

const emit = defineEmits<{
  (e: 'update:active', value: boolean): void
  (e: 'open'): void
  (e: 'close'): void
}>()

const onDisplayClick = () => {
  if (!props.disabled) {
    emit('update:active', true)
    emit('open')
  }
}

const onSave = () => {
  emit('update:active', false)
  emit('close')
}

const onCancel = () => {
  emit('update:active', false)
  emit('close')
}
</script>

<style lang="scss">
.p-inplace {
  display: inline-block;
  position: relative;

  &-display {
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 3px;
    transition: background-color 0.2s;

    &:hover {
      background-color: rgba(0, 0, 0, 0.04);
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 0.2rem rgba(33, 150, 243, 0.25);
    }
  }

  &-content {
    padding: 0.5rem;
  }

  &-actions {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.5rem;

    button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0.5rem;
      border: none;
      border-radius: 3px;
      cursor: pointer;
      transition: background-color 0.2s;

      &:focus {
        outline: none;
        box-shadow: 0 0 0 0.2rem rgba(33, 150, 243, 0.25);
      }

      &.p-inplace-save {
        background-color: var(--primary-color, #2196F3);
        color: var(--primary-color-text, #ffffff);

        &:hover {
          background-color: var(--primary-darker-color, #1976D2);
        }
      }

      &.p-inplace-cancel {
        background-color: var(--danger-color, #dc3545);
        color: var(--danger-color-text, #ffffff);

        &:hover {
          background-color: var(--danger-darker-color, #bd2130);
        }
      }
    }
  }

  &-small {
    font-size: 0.875rem;
    .p-inplace-display,
    .p-inplace-content {
      padding: 0.25rem;
    }
    .p-inplace-actions button {
      padding: 0.25rem;
    }
  }

  &-large {
    font-size: 1.25rem;
    .p-inplace-display,
    .p-inplace-content {
      padding: 0.75rem;
    }
    .p-inplace-actions button {
      padding: 0.75rem;
    }
  }

  &-disabled {
    opacity: 0.6;
    cursor: default;
    pointer-events: none;
  }

  &-responsive {
    @media screen and (max-width: 576px) {
      font-size: 0.875rem;
      .p-inplace-display,
      .p-inplace-content {
        padding: 0.25rem;
      }
      .p-inplace-actions button {
        padding: 0.25rem;
      }
    }
  }

  &-rtl {
    direction: rtl;
    .p-inplace-actions {
      flex-direction: row-reverse;
    }
  }
}
</style>
